import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { useTheme } from "@emotion/react";

import { formatItemVariant, ItemDoc } from "@megaron/catalog-contracts";
import { ItemSortField } from "@megaron/docs-contracts";

type Props = {
  items: ItemDoc[];
  isLoading: boolean;
  activeSortValue: { field: ItemSortField; order?: "ASC" | "DESC" | undefined } | undefined;
  onSortChange: (value: { field: ItemSortField; order?: "ASC" | "DESC" | undefined } | undefined) => void;
};

export const TradeItemsTable: React.FC<Props> = ({ items, isLoading, activeSortValue, onSortChange }) => {
  const theme = useTheme();

  const tableColumns: TableHeadColumn<ItemSortField>[] = [
    ...itemTableColumns.map(({ heading, sortField }) =>
      sortField
        ? ({
            isSortable: true,
            sortFieldName: sortField,
            label: heading,
          } as const)
        : ({
            isSortable: false,
            label: heading,
          } as const),
    ),
  ];

  const getTableRowCellsContent = (item: ItemDoc): RowCellContent[] =>
    itemTableColumns.map((column) => ({
      cellCss: {
        color: item.isArchived || column.tone !== "primary" ? theme.colors.secondaryText : theme.colors.primary,
        opacity: item.isArchived ? 0.5 : 1,
        whiteSpace: column.wrap ? "normal" : "nowrap",
        maxWidth: column.wrap ? "24rem" : undefined,
        minWidth: column.minWidth,
        verticalAlign: column.wrap ? "top" : undefined,
      },
      element: column.getValue(item),
    }));

  return (
    <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
      <Table<ItemSortField>
        columns={tableColumns}
        activeSortValue={activeSortValue}
        onSort={onSortChange}
        css={{ padding: "0 2rem" }}
      >
        <TableBody
          isLoading={isLoading}
          rows={items.map((item) => ({
            uuid: item.itemId,
            cellsContent: getTableRowCellsContent(item),
          }))}
        />
      </Table>
    </div>
  );
};

type ItemTableColumn = {
  heading: string;
  sortField?: ItemSortField;
  tone?: "primary" | "secondary";
  wrap?: boolean;
  minWidth?: string;
  getValue: (item: ItemDoc) => React.ReactNode;
};

const emptyValue = "-";

const formatText = (value: string | null | undefined) => value || emptyValue;
const formatInteger = (value: number | null | undefined) =>
  value === null || value === undefined ? emptyValue : value;
const formatDecimal = (value: { toString: () => string } | null | undefined) => value?.toString() ?? emptyValue;
const formatMoney = (value: { toFixed: (decimalPlaces: number) => string } | null | undefined) =>
  value?.toFixed(2) ?? emptyValue;
const formatBoolean = (value: boolean) => (value ? "Tak" : "Nie");
const formatVariant = (item: ItemDoc) => formatText(formatItemVariant(item.variant));

const DescriptionPreview: React.FC<{ description: string }> = ({ description }) => {
  if (!description) return emptyValue;

  return (
    <span
      title={description}
      css={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        whiteSpace: "normal",
        lineHeight: 1.35,
        maxWidth: "32rem",
      }}
    >
      {description}
    </span>
  );
};

export const itemTableColumns: ItemTableColumn[] = [
  { heading: "Indeks", tone: "primary", getValue: (item) => formatText(item.productId) },
  { heading: "Grupa", tone: "primary", getValue: (item) => formatText(item.productGroup) },
  { heading: "Id", getValue: (item) => formatText(item.itemId) },
  { heading: "Jednostka handlowa", getValue: (item) => formatText(item.itemName), minWidth: "16rem" },
  { heading: "Tytuł", getValue: (item) => formatText(item.title), minWidth: "14rem" },
  { heading: "Wariant", getValue: formatVariant, minWidth: "12rem" },
  { heading: "Marka", getValue: (item) => formatText(item.brand) },
  { heading: "Opis", getValue: (item) => <DescriptionPreview description={item.description} />, wrap: true },
  { heading: "EAN", sortField: "gtin", getValue: (item) => formatText(item.gtin) },
  { heading: "Id BC", getValue: (item) => formatText(item.bcId) },
  { heading: "Handle", getValue: (item) => formatText(item.handle) },
  { heading: "Shopify Product ID", getValue: (item) => formatText(item.shopifyProductId) },
  { heading: "Shopify Variant ID", getValue: (item) => formatText(item.shopifyVariantId) },
  { heading: "Wielkość", sortField: "size", getValue: (item) => formatDecimal(item.size) },
  { heading: "Jednostka wielkości", getValue: (item) => formatText(item.sizeUnit) },
  { heading: "Masa [kg]", getValue: (item) => formatDecimal(item.massKg) },
  { heading: "Paleta", sortField: "pallet", getValue: (item) => formatInteger(item.pallet) },
  { heading: "Warstwa", sortField: "layer", getValue: (item) => formatInteger(item.layer) },
  { heading: "Karton", sortField: "box", getValue: (item) => formatInteger(item.box) },
  { heading: "Koszty standardowe", getValue: (item) => formatMoney(item.cost) },
  { heading: "Domyślna cena", getValue: (item) => formatMoney(item.defaultProfit) },
  { heading: "Zarchiwizowany", getValue: (item) => formatBoolean(item.isArchived) },
  { heading: "Produkt Specjalista", getValue: (item) => formatBoolean(item.isLoyalty) },
  { heading: "W dostępności", getValue: (item) => formatBoolean(item.isInAvailability) },
];
